import { FunctionComponent, memo, useMemo } from "react";
import { Button, SubmitOnChange, Tooltip } from "@edgetier/components";
import { LabeledField } from "@edgetier/client-components";
import { Form, Formik } from "formik";
import { faSearch, faUndo } from "@fortawesome/pro-solid-svg-icons";
import { groupBy } from "@edgetier/utilities";
import { useHistory, useLocation } from "react-router-dom";
import { formatSearch, getInitialValues, getVisibleMetricDistributionFields } from "./explore-filters.utilities";
import { trackUser } from "utilities/track-user";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { getDefaultRouteUrls } from "utilities/get-default-route-urls";
import { IExploreFilters, IProps } from "./explore-filters.types";
import DynamicExploreFilters from "./dynamic-explore-filters";
import FilterBetaWarnings from "./filter-beta-warnings";
import { DateFilter } from "@edgetier/date-filter";
import "./explore-filters.scss";

/**
 * Filters to explore phrases.
 * @param props.defaultFilterValues   An object of values to default to if nothing is parsed from the URL.
 * @param props.hideFields            Names of fields that should not be displayed.
 * @param props.mandatoryFilterValues An object of values that will always be used, regardless of URL values.
 * @param props.onChangeCallback      Any kind of processing for selected filters before submit
 * @param props.submitOnChange        Submit the form on change or have a submit button.
 * @param props.isResetEnabled        Should the reset button be shown. Defaults to true if submit on change if false.
 * @param props.tagGroups             Array of tag groups.
 * @param props.tags                  Array of tags.
 * @param props.trackingEventName     Optional name of the event to track when a user changes the filters.
 * @param props.visibleFields         Optional fields that should be displayed by default.
 * @returns                           Form with text, language, and date inputs.
 */
const ExploreFilters: FunctionComponent<IProps> = ({
    defaultFilterValues = {},
    hideFields = [],
    mandatoryFilterValues = {},
    submitOnChange,
    onChangeCallback,
    customFieldItems = {},
    isResetEnabled = submitOnChange !== true,
    getWarnings,
    limitSelectionMonths,
    tagGroups,
    tags,
    trackingEventName,
    visibleFields = [],
}) => {
    const location = useLocation();
    const history = useHistory();
    const interactionQueryParameters = useInteractionQueryParameters({ defaults: defaultFilterValues });

    // Tags grouped by tag's group.
    const groupedTags = useMemo(() => groupBy(tags, ({ tagGroupId }) => tagGroupId), [tags]);

    const metricDistributionFields = useMemo(
        () => getVisibleMetricDistributionFields(interactionQueryParameters),
        [interactionQueryParameters]
    );

    return (
        <Formik<IExploreFilters>
            initialValues={{
                ...getInitialValues(interactionQueryParameters, groupedTags),
                ...mandatoryFilterValues,
            }}
            onSubmit={(values) => {
                const { search, searchValues } = formatSearch(values, tagGroups, onChangeCallback);
                if (typeof trackingEventName === "string") {
                    trackUser(trackingEventName, searchValues, true);
                }
                history.push({ search });
            }}
        >
            {({ resetForm, values }) => (
                <>
                    <Form className="explore-filters" name="explore-filters" aria-label="explore-filters">
                        {!hideFields.includes("startDateTime") && !hideFields.includes("endDateTime") && (
                            <LabeledField label="Date range">
                                <DateFilter
                                    allowTimeSelect
                                    showResetButton={false}
                                    startDateName="startDateTime"
                                    endDateName="endDateTime"
                                    limitSelectionMonths={limitSelectionMonths}
                                />
                            </LabeledField>
                        )}

                        <DynamicExploreFilters
                            mandatoryFilterValues={mandatoryFilterValues}
                            hideFields={hideFields}
                            customFieldItems={customFieldItems}
                            visibleFields={[...visibleFields, ...metricDistributionFields]}
                            tagGroups={tagGroups}
                        />

                        {submitOnChange && <SubmitOnChange />}

                        {!submitOnChange && (
                            <div className="explore-filters__buttons">
                                <Button className="explore-filters__buttons__submit" icon={faSearch} type="submit">
                                    Search
                                </Button>
                                {isResetEnabled && (
                                    <Tooltip content={"Reset Filters"} delayEnter={750}>
                                        <Button
                                            aria-label="reset filters"
                                            icon={faUndo}
                                            onClick={() => {
                                                const { parameters } = getDefaultRouteUrls()[location.pathname];
                                                const newValues = getInitialValues(
                                                    { ...parameters, ...mandatoryFilterValues },
                                                    groupedTags
                                                );
                                                resetForm({ values: newValues });
                                                const { search } = formatSearch(newValues, tagGroups);
                                                history.push({ search });
                                            }}
                                            isIconOnly
                                            type="submit"
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </Form>
                    {typeof getWarnings === "function" && <FilterBetaWarnings formWarnings={getWarnings(values)} />}
                </>
            )}
        </Formik>
    );
};

export default memo(ExploreFilters);
